import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

// Inhouse icons
import { X } from "@/components/icons_new";

// Utils
import { formatCurrency } from "@/utils/formatCurrency";

export default function RecordPaymentModal({ onClose, order }) {
  const [portalNode, setPortalNode] = useState(null);

  const [selectedMethod, setSelectedMethod] = useState("");
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [notes, setNotes] = useState("");

  // Local payments state
  const [payments, setPayments] = useState(order?.payments || []);

  // Order totals
  const pricing = order?.pricing ?? {};
  const toAmount = (value) =>
    typeof value === "number" && Number.isFinite(value) ? value : 0;

  const total = toAmount(pricing.total);
  const amountPaid = payments.reduce((acc, p) => acc + p.amount, 0);
  const balanceDue = Math.max(total - amountPaid, 0);

  useEffect(() => {
    const node = document.createElement("div");
    node.setAttribute("data-record-payment-modal-root", "true");
    document.body.appendChild(node);
    setPortalNode(node);

    const handleEsc = (event) => {
      if (event.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", handleEsc);

    return () => {
      document.removeEventListener("keydown", handleEsc);
      document.body.removeChild(node);
      setPortalNode(null);
    };
  }, [onClose]);

  if (!portalNode) return null;

  const handleDialogClick = (event) => {
    event.stopPropagation();
  };

  const handleSavePayment = () => {
    if (!selectedMethod || !amount) return;

    const newPayment = {
      paymentId: Date.now().toString(),
      method: selectedMethod,
      amount: Number(amount),
      date,
      notes,
    };

    setPayments((prev) => [...prev, newPayment]);

    // clear form
    setSelectedMethod("");
    setAmount("");
    setNotes("");
    setDate(new Date().toISOString().split("T")[0]);
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[50] flex items-center justify-center bg-[#000]/45 px-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="flex flex-col w-[900px] h-[80%] rounded-lg overflow-hidden bg-white"
        onClick={handleDialogClick}
      >
        {/* Header */}
        <div className="flex items-center justify-between bg-[#ececec] px-6 py-4 rounded-t-lg">
          <span className="font-medium text-[#000]">Record Payment</span>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 transition hover:bg-[#f3f4f6]"
            aria-label="Close modal"
          >
            <X className="h-5 w-5 text-[#333333]" />
          </button>
        </div>

        <div className="flex h-full">
          
          <div className="bg-[#bdbcff29] rounded-[15px] m-[0.7em] flex-1 flex flex-col">
            <div className="flex flex-col items-center justify-center pt-[2em] pb-[1em] gap-[7px]">
              <span className="text-[14px] text-[#3333339a] font-medium">
                Total amount:
              </span>

              <span className="text-[19px] text-[#333333] font-semibold">
                {formatCurrency(total)}
              </span>
            </div>

            <div className="border-t border-[#afafaf9f] my-[1em] mx-[1em]"></div>

            {/* Payment summary */}
            <div className="flex flex-col mx-[1em]">
              <div className="flex justify-between">
                <span className="text-[14px] text-[#3333339a]">Total paid</span>
                <span className="text-[14px] font-medium text-[#333333]">{formatCurrency(amountPaid)}</span>
              </div>

              <div className="flex justify-between">
                <span className="text-[14px] text-[#3333339a]">Balance remaining</span>
                <span className="text-[14px] font-medium text-[#333333]">{formatCurrency(balanceDue)}</span>
              </div>
            </div>

            <div className="border-t border-[#afafaf9f] my-[1em] mx-[1em]"></div>

            {/* Payment history */}
            <div className="m-4 border-b border-[#7b7b7b6d]">
              <span className="text-[15px] font-semibold text-[#333] mb-2 block">
                Recorded Payments
              </span>

              {payments.length > 0 ? (
                <div className="">
                  {payments.map((p) => (
                    <div
                      key={p.paymentId}
                      className="flex justify-between items-center text-sm text-[#333333] font-medium border-t border-[#7b7b7b6d] py-[5px]"
                    >
                      <span>{p.method}</span>
                      <span>{p.date}</span>
                      <span>{formatCurrency(p.amount)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <span className="text-gray-500 text-sm">No payments recorded yet</span>
              )}
            </div>
          </div>

          {/* Record payment section */}
          <div className="bg-white w-[45%] h-full flex flex-col pt-[2em]">
            <div className="flex flex-col gap-[3px] px-6">
              <span className="font-semibold text-[#333333] text-[16px]">
                Record Payment
              </span>
              <span className="font-medium text-[#333333b3] text-[14px]">
                Add details of an already received payment
              </span>
            </div>

            <div className="flex-1 px-6 py-4 space-y-4">
              {/* Payment Method */}
              <div>
                <label className="block text-[#333333] text-sm font-medium mb-1">Payment Method</label>
                <select
                  className="w-full border border-[#3333334e] rounded-md px-3 py-2 text-sm placeholder:text-[#33333388]"
                  value={selectedMethod}
                  onChange={(e) => setSelectedMethod(e.target.value)}
                >
                  <option value="">Select method</option>
                  <option value="Cash">Cash</option>
                  <option value="Mpesa">Mpesa</option>
                  <option value="Card">Credit/Debit</option>
                  <option value="Cheque">Cheque</option>
                  <option value="Bank Transfer">Bank Transfer</option>
                  <option value="Store Credit">Store Credit</option>
                </select>
              </div>

              {/* Amount paid */}
              <div>
                <label className="block text-sm text-[#333333] font-medium mb-1">Amount paid</label>
                <input
                  type="number"
                  className="w-full border border-[#3333334e] rounded-md px-3 py-2 text-sm placeholder:text-[#33333388]"
                  placeholder="Enter amount"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  max={balanceDue}
                />
              </div>

              {/* Date of payment */}
              <div>
                <label className="block text-sm text-[#333333] font-medium mb-1">Date of Payment</label>
                <input
                  type="date"
                  className="w-full border border-[#3333334e] rounded-md px-3 py-2 text-sm placeholder:text-[#33333388]"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  max={new Date().toISOString().split("T")[0]}
                />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm text-[#333333] font-medium mb-1">Notes</label>
                <textarea
                  rows={3}
                  className="w-full border border-[#3333334e] text-[#333333] rounded-md px-3 py-2 text-sm resize-none placeholder:text-[#33333388]"
                  placeholder="Add details e.g. Mpesa code, cheque no."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </div>
            </div>

            {/* Save button */}
            <div className="px-[0.5em] py-[0.2em] mt-auto">
              <button
                onClick={handleSavePayment}
                className="bg-[#333333] w-full rounded-lg text-white font-semibold text-[13px] py-[9px] mb-[20px] hover:bg-[#333333db]"
              >
                Save Payment
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    portalNode
  );
}
